import React, { useEffect, useState } from "react";
import { t } from "ttag";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { useDetectAudio } from "../../utils/useDetectAudio";
import { Svg } from "../Svg";

import styles from "./VideoPlayerControls.module.scss";

interface Props {
    video: HTMLVideoElement | null;
    wrapperAttrs?: React.HTMLAttributes<HTMLDivElement>;
}

export const VideoPlayerControls = ({ video, wrapperAttrs }: Props) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const hasAudio = useDetectAudio(video);

    useEffect(() => {
        if (!video) return;
        const syncPlaying = () => setIsPlaying(!video.paused);
        const syncMuted = () => setIsMuted(video.muted);
        syncPlaying();
        syncMuted();
        video.addEventListener("play", syncPlaying);
        video.addEventListener("pause", syncPlaying);
        video.addEventListener("volumechange", syncMuted);
        return () => {
            video.removeEventListener("play", syncPlaying);
            video.removeEventListener("pause", syncPlaying);
            video.removeEventListener("volumechange", syncMuted);
        };
    }, [video]);

    if (!video) return null;

    const handlePlayPause = () => {
        if (video.paused) {
            video.play().catch(() => {});
        } else {
            video.pause();
        }
    };

    const handleMuteToggle = () => {
        // eslint-disable-next-line react-hooks/immutability -- DOM elements are external resources; muting via property is the standard API
        video.muted = !video.muted;
    };

    return (
        <div
            {...wrapperAttrs}
            className={concatClassNames([
                styles.controls,
                wrapperAttrs?.className,
            ])}
        >
            <button
                type="button"
                className={styles.control}
                onClick={handlePlayPause}
                aria-label={isPlaying ? t`Pause video` : t`Play video`}
            >
                <Svg name={isPlaying ? "video/pause" : "video/play"} />
            </button>
            {hasAudio && (
                <button
                    type="button"
                    className={styles.control}
                    onClick={handleMuteToggle}
                    aria-label={isMuted ? t`Unmute video` : t`Mute video`}
                >
                    <Svg name={isMuted ? "audio off" : "audio on"} />
                </button>
            )}
        </div>
    );
};
